import { GetServerSidePropsContext } from 'next'
import { GrowthCategory, GrowthContent, Product } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { reportError } from '@mc/next-newrelic'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import type { SubcategoryDynamicHeroValues } from 'libs/dynamic-hero'
import { ssrSubcategoryDynamicHero } from 'libs/dynamic-hero'
import {
  getDefaultProduct,
  getInstructorsForSubcatSlug,
  getOutcomeToContentsMapBySubcat,
  getSubcategoryRankedCourseSlugs,
  getSubcategoryToOutcomesMap,
} from 'libs/server/api'
import {
  CategoryPageCourse,
  getCoursesByCategoryIdFromGrowthAPI,
  getGrowthPrimaryCategoryBySubcategorySlug,
  getGrowthSubcategoryBySlug,
  getV2Categories,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { useSprig } from 'libs/sprig'
import { CourseMeta, Outcome, SubcategoryWithOutcome } from 'types/data'
import { getOutcomeSlugSorted } from '../../libs/utils/outcomes/getOutcomeSlugSorted'
import { Meta } from './components/Meta'
import { SubcategoryPage } from './components/SubcategoryPage/SubcategoryPage'
import { VideoContextProvider } from './components/VideoContext'

export interface SubcategoriesProps {
  slug: string
  subcatToOutcomes: SubcategoryWithOutcome
  outcomesBySlug: Record<string, Outcome>
  courseSlugToMeta: Record<string, CourseMeta>
  instructors: CourseMeta[]
  subcatHeroCourses: GrowthContent[]
  primaryCategoryCourses: CategoryPageCourse[]
  primaryCategory: GrowthCategory | null
  outcomeRanking: string[]
  annualPass: Product
  subcategorySavedDynamicHero?: SubcategoryDynamicHeroValues
}

const Subcategories = ({
  slug,
  subcatToOutcomes,
  outcomesBySlug,
  instructors,
  courseSlugToMeta,
  primaryCategory,
  primaryCategoryCourses,
  outcomeRanking,
  annualPass,
  subcatHeroCourses,
  subcategorySavedDynamicHero,
}: SubcategoriesProps) => {
  useSprig()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'Subcategory' }}>
      <StaticDataProvider
        value={{
          subcatSlug: slug,
          annualPass,
          subcatToOutcomes,
          instructors,
          outcomesBySlug,
          subcatHeroCourses,
          courseSlugToMeta,
          primaryCategory,
          primaryCategoryCourses,
          outcomeRanking,
          subcategorySavedDynamicHero,
        }}
        providerKey={`subcat-${slug}`}
      >
        <VideoContextProvider>
          <PromoExplainerModal pageSource={PAGE_SOURCES.SUBCATEGORY} />
          <BogoBanner />
          <Meta />
          <SubcategoryPage />
        </VideoContextProvider>
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

Subcategories.pageSettings = {
  rumLabel: 'Subcategory',
  analyticsLabel: 'Subcategory',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const slug = ctx.query.slug as string

  const subcategory = await getGrowthSubcategoryBySlug(ctx, slug)
  const primaryCategory = await getGrowthPrimaryCategoryBySubcategorySlug(
    ctx,
    slug,
  )

  if (!subcategory) {
    return { notFound: true }
  }

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)
  await trackPen7694BackendEV(ctx)

  try {
    const [
      subcatToOutcomes,
      { outcomesBySlug, courseSlugToMeta },
      { savedDynamicHero: subcategorySavedDynamicHero },
    ] = await Promise.all([
      getSubcategoryToOutcomesMap(ctx, slug) as Promise<SubcategoryWithOutcome>,
      getOutcomeToContentsMapBySubcat(ctx, slug),
      ssrSubcategoryDynamicHero(ctx, slug),
    ])

    const outcomeRanking = getOutcomeSlugSorted(
      subcatToOutcomes.outcomeSlugs,
      subcatToOutcomes.outcomeScores,
    )

    return getPageData({
      nonCriticalPromises: {
        annualPass: getDefaultProduct(ctx),
        categories: getV2Categories(ctx),
        primaryCategoryCourses: getCoursesByCategoryIdFromGrowthAPI(
          ctx,
          primaryCategory?.id ?? -1,
        ).catch(() => []),
        subcatHeroCourses: getSubcategoryRankedCourseSlugs(
          ctx,
          subcategory.id,
        ).then((courses) => courses.slice(0, 2)),
        instructors: getInstructorsForSubcatSlug(ctx, slug),
      },
      propsMapper: ({ ...restProps }) => ({
        slug,
        subcatToOutcomes,
        outcomesBySlug,
        courseSlugToMeta,
        outcomeRanking,
        primaryCategory,
        subcategorySavedDynamicHero,
        ...restProps,
      }),
    })
  } catch (e) {
    reportError(e as Error, { slug })
    return pageErrorHandler(e)
  }
}

export default Subcategories
